import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from 'lucide-react';
import AnimateOnScroll from './AnimateOnScroll';
import SectionHeader from './SectionHeader';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: '',
  });
  const [focused, setFocused] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  const contactInfo = [
    { icon: Phone, title: 'Phone Number', details: ['08066312634', '+234 806 631 2634'], action: 'tel:08066312634', cta: 'Call Now' },
    { icon: MapPin, title: 'Address', details: ['Jesse Jackson Street', 'Abuja, Nigeria'], action: '#', cta: null },
    { icon: Clock, title: 'Business Hours', details: ['Mon – Fri: 8AM – 6PM', 'Sat: 9AM – 4PM'], action: '#', cta: null },
    { icon: Mail, title: 'Email', details: ['info@sharpcars.ng', 'sales@sharpcars.ng'], action: 'mailto:info@sharpcars.ng', cta: 'Send Email' },
  ];

  const inputClass = (name) =>
    `w-full px-4 py-3.5 bg-white/5 border rounded-xl text-white placeholder-gray-500 transition-all duration-300 ${
      focused === name
        ? 'border-[#3DED97] ring-1 ring-[#3DED97]/30 bg-white/[0.07]'
        : 'border-white/10 hover:border-white/20'
    }`;

  return (
    <section id="contact" className="relative py-28 section-mesh overflow-hidden">
      <div className="absolute inset-0 section-grid-bg opacity-20 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          badge="Reach Out"
          title="Get in"
          highlight="Touch"
          description="Ready to experience luxury? Schedule a consultation, view our inventory, or learn more about our services."
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <AnimateOnScroll animation="fade-right" className="lg:col-span-3">
            <div className="premium-card glass-card p-8 sm:p-10 h-full">
              <h3 className="font-display text-2xl font-bold text-white mb-2">Send us a Message</h3>
              <p className="text-gray-500 text-sm mb-8">We&apos;ll get back to you within 24 hours.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Full Name *</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange}
                      onFocus={() => setFocused('name')} onBlur={() => setFocused('')} required
                      className={inputClass('name')} placeholder="John Doe" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Email *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                      onFocus={() => setFocused('email')} onBlur={() => setFocused('')} required
                      className={inputClass('email')} placeholder="john@email.com" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Phone</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange}
                      onFocus={() => setFocused('phone')} onBlur={() => setFocused('')}
                      className={inputClass('phone')} placeholder="0806 000 0000" />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Subject *</label>
                    <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange}
                      onFocus={() => setFocused('subject')} onBlur={() => setFocused('')} required
                      className={inputClass('subject')} placeholder="Vehicle inquiry" />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Message *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange}
                    onFocus={() => setFocused('message')} onBlur={() => setFocused('')} required rows="5"
                    className={`${inputClass('message')} resize-none`} placeholder="Tell us about your dream car..." />
                </div>

                <button type="submit" className="btn-primary !w-full !rounded-xl !py-4">
                  <Send className="h-5 w-5" />
                  Send Message
                </button>
              </form>
            </div>
          </AnimateOnScroll>

          <div className="lg:col-span-2 space-y-4">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <AnimateOnScroll key={index} animation="fade-left" delay={index * 80}>
                  <div className="premium-card glass-card p-6 group">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl gradient-bg text-black group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-white mb-2">{info.title}</h4>
                        {info.details.map((d, di) => (
                          <p key={di} className="text-gray-400 text-sm mb-0.5">{d}</p>
                        ))}
                        {info.cta && (
                          <a href={info.action} className="inline-flex items-center gap-1.5 text-[#3DED97] hover:text-[#B0FC38] text-sm font-medium mt-3 transition-colors">
                            {info.cta}
                            <MessageCircle className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </AnimateOnScroll>
              );
            })}
          </div>
        </div>

        <AnimateOnScroll animation="fade-up" delay={150}>
          <div className="mt-12 premium-card glass-card p-8 sm:p-10">
            <h3 className="font-display text-2xl font-bold text-white mb-6 text-center">Visit Our Showroom</h3>
            <div className="relative h-72 sm:h-80 rounded-2xl overflow-hidden bg-gray-900 flex items-center justify-center">
              <div className="absolute inset-0 section-grid-bg opacity-50" />
              <div className="relative text-center">
                <div className="inline-flex p-5 rounded-full bg-[#3DED97]/10 mb-4 animate-float-slow">
                  <MapPin className="h-10 w-10 text-[#3DED97]" />
                </div>
                <p className="text-xl font-display font-bold text-white">Jesse Jackson Street, Abuja</p>
                <p className="text-gray-500 text-sm mt-1">Nigeria</p>
              </div>
            </div>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll animation="scale-up" delay={200}>
          <div className="mt-12 text-center p-10 rounded-3xl border border-[#3DED97]/15 bg-gradient-to-r from-[#3DED97]/5 to-[#B0FC38]/5">
            <h3 className="font-display text-2xl font-bold text-white mb-3">Need Immediate Assistance?</h3>
            <p className="text-gray-400 mb-6">Call us directly for instant support and consultation.</p>
            <a href="tel:08066312634" className="btn-primary">
              <Phone className="h-5 w-5" />
              Call 08066312634
            </a>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default Contact;
